import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { useNavigate } from 'react-router-dom';

import { communicationEquipmentApi } from '../../api/communicationEquipment';
import { downloadBlobAsFile } from '../../utils/download';
import PulseRegistrarImportDialog from './PulseRegistrarImportDialog';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const emptyModuleForm = {
  name: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  uplink_transport: 'lorawan',
  dev_eui: '',
  network_address: '',
  channel_count: 8,
  notes: '',
};

const unitLabel = {
  m3: 'м³',
  kwh: 'кВт·ч',
  gcal: 'Гкал',
  l: 'л',
};

function formatWeight(channel) {
  if (channel?.pulse_weight == null) return null;
  const unit = unitLabel[channel.pulse_unit] || channel.pulse_unit || '';
  const value = Number(channel.pulse_weight);
  if (!Number.isFinite(value)) return null;
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 6 })} ${unit}/имп.`;
}

function formatLastData(value) {
  if (!value) return 'нет данных';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('ru-RU');
  } catch {
    return String(value);
  }
}

function channelHealthTone(channel) {
  const occupied = Boolean(channel.device);
  if (!occupied) {
    return {
      key: 'free',
      dot: '#CBD5E1',
      border: 'rgba(38, 65, 94, 0.10)',
      background: 'linear-gradient(180deg, #FFFFFF 0%, #F7F9FC 100%)',
    };
  }
  const status = channel.device.status;
  const problem = Boolean(channel.device.is_problem);
  const missingWeight = channel.pulse_weight == null;
  if (problem || status === 'error') {
    return {
      key: 'error',
      dot: '#DC2626',
      border: 'rgba(220, 38, 38, 0.35)',
      background: 'linear-gradient(180deg, #FEF2F2 0%, #FEE2E2 100%)',
    };
  }
  if (status === 'warn' || status === 'no_data' || missingWeight) {
    return {
      key: 'warn',
      dot: '#D97706',
      border: 'rgba(217, 119, 6, 0.35)',
      background: 'linear-gradient(180deg, #FFF9F0 0%, #FFF4E5 100%)',
    };
  }
  return {
    key: 'ok',
    dot: '#0F9F6E',
    border: 'rgba(15, 159, 110, 0.28)',
    background: 'linear-gradient(180deg, #F3FBF7 0%, #EAF8F1 100%)',
  };
}

function deviceStatusLabel(status, isProblem) {
  if (!status && isProblem) return 'нет данных';
  if (status === 'ok') return 'ok';
  if (status === 'warn' || status === 'no_data') return 'нет показаний';
  if (status === 'error') return 'ошибка';
  if (status === 'NEW') return 'новый';
  return status || '—';
}

function pathFocusChipColor(focus) {
  if (focus === 'gateway') return 'error';
  if (focus === 'devices' || focus === 'mixed') return 'warning';
  if (focus === 'ok') return 'success';
  return 'default';
}

function ChannelCell({ channel, onOpenApartment }) {
  const occupied = Boolean(channel.device);
  const tone = channelHealthTone(channel);
  const label = occupied
    ? `кв. ${channel.device.apartment_number || '—'} · ${channel.device.system_type || 'прибор'}`
    : 'свободен';
  const weightText = formatWeight(channel);
  const tooltip = occupied
    ? [
        channel.device.serial_number || 'без серийника',
        `статус: ${deviceStatusLabel(channel.device.status, channel.device.is_problem)}`,
        `данные: ${formatLastData(channel.device.last_data_time)}`,
        weightText || 'вес не настроен',
      ].join(' · ')
    : 'Свободный канал';

  return (
    <Tooltip arrow title={tooltip}>
      <Box
        component={occupied && channel.device.apartment_id ? 'button' : 'div'}
        type={occupied && channel.device.apartment_id ? 'button' : undefined}
        onClick={
          occupied && channel.device.apartment_id
            ? () => onOpenApartment(channel.device.apartment_id)
            : undefined
        }
        sx={{
          all: 'unset',
          display: 'grid',
          gap: 0.35,
          p: 1,
          minHeight: 72,
          borderRadius: 2.5,
          cursor: occupied && channel.device.apartment_id ? 'pointer' : 'default',
          border: '1px solid',
          borderColor: tone.border,
          background: tone.background,
          transition: 'transform 160ms ease, box-shadow 160ms ease',
          '&:hover': occupied && channel.device.apartment_id
            ? { transform: 'translateY(-1px)', boxShadow: '0 8px 18px rgba(31, 53, 80, 0.08)' }
            : undefined,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#64748B', letterSpacing: 0.4 }}>
            CH {channel.number}
          </Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: tone.dot }} />
        </Stack>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1F3550', lineHeight: 1.2 }}>
          {label}
        </Typography>
        {occupied ? (
          <Typography sx={{ fontSize: 11, color: '#64748B', lineHeight: 1.2 }}>
            {deviceStatusLabel(channel.device.status, channel.device.is_problem)}
            {channel.device.serial_number ? ` · ${channel.device.serial_number}` : ''}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
}

function ModuleCard({ module, expanded, onToggle, onOpenApartment, channelQuery }) {
  const free = Math.max(0, module.channel_count - module.occupied_channels);
  const [detail, setDetail] = useState(null);
  const [loadedForId, setLoadedForId] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!expanded) return undefined;
    if (loadedForId === module.id) return undefined;
    setLoadingDetail(true);
    setDetailError('');
    communicationEquipmentApi
      .getRadioModule(module.id)
      .then((payload) => {
        if (!cancelled) {
          setDetail(payload);
          setLoadedForId(module.id);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDetailError(err?.response?.data?.detail || 'Не удалось загрузить каналы модуля');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, module.id, loadedForId]);

  const channels = useMemo(() => {
    const source = detail?.channels || [];
    const list = [...source].sort((a, b) => a.number - b.number);
    if (!channelQuery) return list;
    const q = channelQuery.toLowerCase();
    return list.filter((channel) => {
      if (String(channel.number).includes(q)) return true;
      if (!channel.device) return q.includes('свобод') || q.includes('free');
      return [
        channel.device.apartment_number,
        channel.device.serial_number,
        channel.device.system_type,
        channel.device.status,
      ].some((value) => String(value || '').toLowerCase().includes(q));
    });
  }, [detail?.channels, channelQuery]);

  const problemChannels = useMemo(
    () => (detail?.channels || []).filter((channel) => channel.device?.is_problem),
    [detail?.channels]
  );

  const showDiagnosis =
    module.path_issue_focus &&
    module.path_issue_focus !== 'unknown' &&
    (module.occupied_channels > 0 || module.uplink_gateway_status);

  return (
    <Box
      sx={{
        borderRadius: 3.5,
        border: '1px solid rgba(38, 65, 94, 0.12)',
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1.25, cursor: 'pointer' }}
        onClick={onToggle}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(180deg, #EEF5FF 0%, #E4EFFC 100%)',
            color: '#2E73B8',
          }}
        >
          <RouterRoundedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 900, color: '#1F3550' }}>{module.name}</Typography>
            <Chip size="small" label={`${module.occupied_channels}/${module.channel_count}`} sx={{ fontWeight: 800 }} />
            <Chip
              size="small"
              variant="outlined"
              label={module.uplink_transport === 'mqtt' ? 'MQTT' : 'LoRaWAN'}
            />
            {free > 0 ? (
              <Chip size="small" color="success" variant="outlined" label={`${free} свободно`} />
            ) : (
              <Chip size="small" color="warning" variant="outlined" label="заполнен" />
            )}
            {(module.devices_problem || 0) > 0 ? (
              <Chip size="small" color="error" variant="outlined" label={`проблем: ${module.devices_problem}`} />
            ) : null}
            {showDiagnosis ? (
              <Chip
                size="small"
                color={pathFocusChipColor(module.path_issue_focus)}
                label={module.path_issue_label || module.path_issue_focus}
              />
            ) : null}
          </Stack>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            {[module.manufacturer, module.model, module.serial_number, module.dev_eui || module.network_address]
              .filter(Boolean)
              .join(' · ') || 'без доп. данных'}
          </Typography>
        </Box>
        <IconButton
          size="small"
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 160ms ease',
          }}
        >
          <ExpandMoreRoundedIcon />
        </IconButton>
      </Stack>

      {expanded && problemChannels.length > 0 ? (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {problemChannels.slice(0, 8).map((channel) => (
              <Chip
                key={channel.id}
                size="small"
                color="error"
                variant="outlined"
                label={`CH ${channel.number}: кв. ${channel.device?.apartment_number || '—'}`}
                sx={{ fontWeight: 700 }}
              />
            ))}
            {problemChannels.length > 8 ? (
              <Chip size="small" variant="outlined" label={`+${problemChannels.length - 8}`} />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <Collapse in={expanded}>
        <Box sx={{ p: 1.5, pt: 0 }}>
          {loadingDetail ? (
            <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : channels.length ? (
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
              }}
            >
              {channels.map((channel) => (
                <ChannelCell key={channel.id} channel={channel} onOpenApartment={onOpenApartment} />
              ))}
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
              Нет каналов по текущему фильтру.
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function CreateRadioModuleDialog({ open, buildingId, onClose, onCreated }) {
  const [form, setForm] = useState(emptyModuleForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(emptyModuleForm);
      setError('');
    }
  }, [open]);

  const valid = useMemo(() => {
    if (!form.name.trim()) return false;
    const channels = Number(form.channel_count);
    if (!Number.isFinite(channels) || channels < 1 || channels > 64) return false;
    if (form.uplink_transport === 'lorawan') {
      const deveui = form.dev_eui.replace(/[\s:-]/g, '');
      return deveui.length === 16;
    }
    return Boolean(form.network_address.trim());
  }, [form]);

  const handleCreate = async () => {
    if (!buildingId || !valid) return;
    setSaving(true);
    setError('');
    try {
      const created = await communicationEquipmentApi.createRadioModule({
        building_id: buildingId,
        name: form.name.trim(),
        manufacturer: form.manufacturer.trim() || undefined,
        model: form.model.trim() || undefined,
        serial_number: form.serial_number.trim() || undefined,
        uplink_transport: form.uplink_transport,
        channel_count: Number(form.channel_count),
        dev_eui: form.uplink_transport === 'lorawan' ? form.dev_eui.trim() : undefined,
        network_address: form.uplink_transport === 'mqtt' ? form.network_address.trim() : undefined,
        notes: form.notes.trim() || undefined,
      });
      onCreated?.(created);
      onClose?.();
    } catch (createError) {
      setError(createError?.response?.data?.detail || 'Не удалось создать радиомодуль');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Новый радиомодуль</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Импульсный регистратор с несколькими каналами. После создания каналы появятся на карте дома.
          </Alert>
          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                size="small"
                label="Название"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="Подъезд 1, шкаф А"
                required
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Передача данных"
                value={form.uplink_transport}
                onChange={(e) => setForm((s) => ({ ...s, uplink_transport: e.target.value }))}
              >
                <MenuItem value="lorawan">LoRaWAN</MenuItem>
                <MenuItem value="mqtt">MQTT</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Производитель"
                value={form.manufacturer}
                onChange={(e) => setForm((s) => ({ ...s, manufacturer: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Модель"
                value={form.model}
                onChange={(e) => setForm((s) => ({ ...s, model: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Серийный номер"
                value={form.serial_number}
                onChange={(e) => setForm((s) => ({ ...s, serial_number: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              {form.uplink_transport === 'lorawan' ? (
                <TextField
                  fullWidth
                  size="small"
                  label="DevEUI"
                  value={form.dev_eui}
                  onChange={(e) => setForm((s) => ({ ...s, dev_eui: e.target.value }))}
                  required
                  helperText="16 hex-символов"
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="MQTT-топик"
                  value={form.network_address}
                  onChange={(e) => setForm((s) => ({ ...s, network_address: e.target.value }))}
                  required
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Каналов"
                value={form.channel_count}
                onChange={(e) => setForm((s) => ({ ...s, channel_count: e.target.value }))}
                inputProps={{ min: 1, max: 64, step: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Комментарий"
                value={form.notes}
                onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={handleCreate}>
          {saving ? 'Создание…' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BuildingChannelMap({ buildingId }) {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [totals, setTotals] = useState({
    modules: 0,
    channels: 0,
    occupied: 0,
    free: 0,
    missing_weight: 0,
    with_problems: 0,
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [occupancy, setOccupancy] = useState('all');
  const [uplinkTransport, setUplinkTransport] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');
  const [expanded, setExpanded] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, occupancy, uplinkTransport, activeFilter, pageSize, buildingId]);

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      const response = await communicationEquipmentApi.listRadioModules(buildingId, {
        detail: 'summary',
        q: debouncedQuery || undefined,
        occupancy,
        uplink_transport: uplinkTransport === 'all' ? undefined : uplinkTransport,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
        page,
        page_size: pageSize,
      });
      setModules(response.items || []);
      setTotal(response.total || 0);
      setTotals(response.totals || {
        modules: 0,
        channels: 0,
        occupied: 0,
        free: 0,
        missing_weight: 0,
        with_problems: 0,
      });
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || 'Не удалось загрузить карту каналов.');
    } finally {
      setLoading(false);
    }
  }, [buildingId, debouncedQuery, occupancy, uplinkTransport, activeFilter, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const handleExport = async () => {
    setBusy(true);
    try {
      const blob = await communicationEquipmentApi.exportChannelMap(buildingId);
      downloadBlobAsFile(blob, 'pulse_channel_map.xlsx');
    } catch (exportError) {
      setError(exportError?.response?.data?.detail || 'Не удалось выгрузить карту каналов.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(38, 65, 94, 0.12)',
          background: 'linear-gradient(135deg, #1F3550 0%, #2E73B8 58%, #3B8FD4 100%)',
          color: '#fff',
          p: { xs: 2, md: 2.5 },
          display: 'grid',
          gap: 1.5,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 22, lineHeight: 1.15 }}>
              Каналы дома
            </Typography>
            <Typography sx={{ opacity: 0.84, mt: 0.5, maxWidth: 560 }}>
              Радиомодули и занятость каналов. Создавайте модули вручную, импортируйте Excel или выгружайте карту.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: 2.5,
                fontWeight: 800,
                boxShadow: 'none',
                bgcolor: '#fff',
                color: '#1F3550',
                '&:hover': { bgcolor: '#F3F7FC' },
              }}
            >
              Добавить модуль
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setImportOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: 2.5,
                fontWeight: 800,
                borderColor: 'rgba(255,255,255,0.45)',
                color: '#fff',
              }}
            >
              Импорт Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadRoundedIcon />}
              disabled={busy}
              onClick={handleExport}
              sx={{
                textTransform: 'none',
                borderRadius: 2.5,
                fontWeight: 800,
                borderColor: 'rgba(255,255,255,0.45)',
                color: '#fff',
              }}
            >
              Выгрузить карту
            </Button>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[
            ['Модулей', totals.modules],
            ['Каналов', totals.channels],
            ['Занято', totals.occupied],
            ['Свободно', totals.free],
            ['Без веса импульса', totals.missing_weight],
            ['С проблемами', totals.with_problems],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                px: 1.25,
                py: 0.85,
                minWidth: 96,
                borderRadius: 2.5,
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.16)',
              }}
            >
              <Typography sx={{ fontSize: 11, opacity: 0.8 }}>{label}</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.1 }}>{value}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          borderRadius: 3.5,
          border: '1px solid rgba(38, 65, 94, 0.12)',
          background: '#fff',
          p: 1.5,
          display: 'grid',
          gap: 1.25,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            placeholder="Поиск: модуль, DevEUI, квартира, серийник…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: '#94A3B8' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Занятость</InputLabel>
            <Select
              label="Занятость"
              value={occupancy}
              onChange={(e) => setOccupancy(e.target.value)}
            >
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="has_free">Со свободными</MenuItem>
              <MenuItem value="full">Полностью занятые</MenuItem>
              <MenuItem value="has_problems">С проблемами</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Транспорт</InputLabel>
            <Select
              label="Транспорт"
              value={uplinkTransport}
              onChange={(e) => setUplinkTransport(e.target.value)}
            >
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="lorawan">LoRaWAN</MenuItem>
              <MenuItem value="mqtt">MQTT</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Статус</InputLabel>
            <Select
              label="Статус"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="active">Активные</MenuItem>
              <MenuItem value="inactive">Неактивные</MenuItem>
              <MenuItem value="all">Все</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>На стр.</InputLabel>
            <Select
              label="На стр."
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Найдено модулей: {total}. Каналы подгружаются при раскрытии карточки.
        </Typography>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}

      {loading ? (
        <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : modules.length === 0 ? (
        <Box
          sx={{
            borderRadius: 4,
            border: '1px dashed rgba(38, 65, 94, 0.18)',
            background: '#fff',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 900, color: '#1F3550', mb: 0.5 }}>
            Пока пусто
          </Typography>
          <Typography sx={{ color: '#64748B', mb: 2 }}>
            Добавьте радиомодуль вручную или загрузите Excel-карту каналов.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2.5, fontWeight: 800, boxShadow: 'none' }}
            >
              Добавить модуль
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setImportOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2.5, fontWeight: 800 }}
            >
              Импорт Excel
            </Button>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {modules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              channelQuery={debouncedQuery}
              expanded={Boolean(expanded[module.id])}
              onToggle={() =>
                setExpanded((current) => ({ ...current, [module.id]: !current[module.id] }))
              }
              onOpenApartment={(apartmentId) => navigate(`/apartments/${apartmentId}`)}
            />
          ))}
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Страница {page} из {pageCount}
            </Typography>
            <Pagination
              color="primary"
              page={page}
              count={pageCount}
              onChange={(_event, next) => setPage(next)}
            />
          </Stack>
        </Stack>
      )}

      <PulseRegistrarImportDialog
        open={importOpen}
        buildingId={buildingId}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setImportOpen(false);
          load();
        }}
      />
      <CreateRadioModuleDialog
        open={createOpen}
        buildingId={buildingId}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setExpanded((current) => ({ ...current, [created.id]: true }));
          load();
        }}
      />
    </Box>
  );
}
